import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Textarea } from "@/common/components/ui/textarea";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Building2, Globe } from "lucide-react";
import { TabProps } from "../types";

const BasicDetailsTab = ({
  formData,
  handleInputChange,
  validationErrors = {},
  readOnly = false,
}: TabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
      <div className="space-y-6 md:col-span-8">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <Building2 className="h-4 w-4 text-slate-500" />
              Company Identity
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-x-8 gap-y-6 p-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Company Name *
              </Label>
              <Input
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
                disabled={readOnly}
                placeholder={formData.company_name || "Full Legal Name"}
                className={`h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.company_name
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.company_name && (
                <p className="text-xs text-red-600">
                  {validationErrors.company_name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Brand / Short Name *
              </Label>
              <Input
                name="company_short_name"
                value={formData.company_short_name}
                onChange={handleInputChange}
                required
                disabled={readOnly}
                placeholder={formData.company_short_name || "e.g. Zomato"}
                className={`h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.company_short_name
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.company_short_name && (
                <p className="text-xs text-red-600">
                  {validationErrors.company_short_name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Industry Sector
              </Label>
              <Input
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder="e.g. Technology"
                className={`h-10 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Business Description
              </Label>
              <Textarea
                name="about_company"
                rows={4}
                value={formData.about_company}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder="Write a brief overview of the company's business model and history..."
                className={`resize-none border-slate-200 leading-relaxed transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <Globe className="h-4 w-4 text-slate-500" />
              Company Contact & Headquarters
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-6 p-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Headquarters
              </Label>
              <Input
                name="headquarters"
                value={formData.headquarters}
                onChange={handleInputChange}
                disabled={readOnly}
                className={`h-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Company Phone
              </Label>
              <Input
                name="company_phone"
                value={formData.company_phone}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder={formData.company_phone || "e.g. +91-1234567890"}
                className={`h-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.company_phone
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.company_phone && (
                <p className="text-xs text-red-600">
                  {validationErrors.company_phone}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Company Email
              </Label>
              <Input
                name="company_email"
                type="email"
                value={formData.company_email}
                onChange={handleInputChange}
                disabled={readOnly}
                placeholder={
                  formData.company_email || "e.g. contact@company.com"
                }
                className={`h-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                  validationErrors.company_email
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              {validationErrors.company_email && (
                <p className="text-xs text-red-600">
                  {validationErrors.company_email}
                </p>
              )}
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Registered Office Address
              </Label>
              <Textarea
                name="company_address"
                rows={2}
                value={formData.company_address}
                onChange={handleInputChange}
                disabled={readOnly}
                className={`resize-none placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BasicDetailsTab;
